import { fetcher } from 'components/shared/fetcher.js'

export type LoginStep1Request = {
  email: string
  password: string
  remember: boolean
  return_url: string
}

export type LoginStep2Request = {
  mfaToken: string
  mfaCode: string
}

export type LoginResendRequest = {
  mfaToken: string
}

export type LoginApiConfig = {
  apiBaseUrl?: string
}

export type Step1Data = {
  success?: boolean
  mfa_token?: string
  /** Server-chosen redirect target after login completes (preferred over client props), or the change password page when `password_expired` is set. */
  return_url?: string
  /** User must change the password before logging in (non-MFA shops only); navigate to `return_url`. */
  password_expired?: boolean
  [key: string]: unknown
}

export type Step2Data = {
  success?: boolean
  /** Post-login redirect target, or the change password page when `password_expired` is set. */
  return_url?: string
  /** User must change the password before logging in; navigate to `return_url`. */
  password_expired?: boolean
  [key: string]: unknown
}

export type ResendData = {
  [key: string]: unknown
}

export type LoginApiResponse<T> = {
  ok: boolean
  status: number
  statusText: string
  data: T | null
}

const parseDataFromResponse = async (response: Response): Promise<unknown> => {
  const nullBodyStatuses = [101, 103, 204, 205, 304]
  if (nullBodyStatuses.includes(response.status)) return null

  const text = await response.text()
  if (!text) return null

  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const createJsonRequest = (body: Record<string, unknown>): RequestInit => ({
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(body),
})

const buildApiUrl = (path: string, apiBaseUrl = ''): URL => {
  const baseUrl = apiBaseUrl || window.location.origin
  return new URL(path, baseUrl)
}

export const loginStep1 = async (
  request: LoginStep1Request,
  config: LoginApiConfig = {},
): Promise<LoginApiResponse<Step1Data>> => {
  const response = await fetcher.fetch(
    buildApiUrl('/nl_NL/api/v1/authentication/mfa/login/step1/', config.apiBaseUrl),
    createJsonRequest({
      email: request.email,
      password: request.password,
      remember: request.remember,
      return_url: request.return_url,
    })
  )

  const data = await parseDataFromResponse(response) as Step1Data | null
  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data,
  }
}

export const loginStep2 = async (
  request: LoginStep2Request,
  config: LoginApiConfig = {},
): Promise<LoginApiResponse<Step2Data>> => {
  const response = await fetcher.fetch(
    buildApiUrl('/nl_NL/api/v1/authentication/mfa/login/step2/', config.apiBaseUrl),
    createJsonRequest({
      mfa_token: request.mfaToken,
      mfa_code: request.mfaCode,
    })
  )

  const data = await parseDataFromResponse(response) as Step2Data | null
  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data,
  }
}

export const resendMfaCode = async (
  request: LoginResendRequest,
  config: LoginApiConfig = {},
): Promise<LoginApiResponse<ResendData>> => {
  const response = await fetcher.fetch(
    buildApiUrl('/nl_NL/api/v1/authentication/mfa/resend/', config.apiBaseUrl),
    createJsonRequest({
      mfa_token: request.mfaToken,
    })
  )

  const data = await parseDataFromResponse(response) as ResendData | null
  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data,
  }
}
